import { Controller } from "@hotwired/stimulus";

// 마켓 미리보기 결과는 잡이 웹소켓(turbo_stream_from)으로 보내 준다.
// 웹소켓 구독이 안 붙었거나 결과가 늦으면, 잡이 남겨 둔 결과를 HTTP 로 가져온다
// (Market::FontsController#preview_result). 그래도 끝내 안 오면 끝없는 로딩 대신 실패 문구를 보인다.
//
// 웹소켓 한 곳이 막혔다고 구매 전 미리보기가 멈추면 안 된다.
// (2026-09-29 웹 파드 하나의 구독 리스너가 죽어 그 파드로 간 방문자는 42시간 동안 로딩만 봤다.)
const WAIT_FOR_SOCKET_MS = 3000;
// submit-end 시점에는 서버가 준 "로딩 중" 화면이 아직 안 그려져 있다(Turbo 는 다음 프레임에 그린다).
// 그 전에 보면 로딩 중인 굵기가 없다고 여기고 그만두므로, 웹소켓이 없어도 이만큼은 기다린다.
const WAIT_FOR_RENDER_MS = 500;
const POLL_INTERVAL_MS = 1500;
const GIVE_UP_MS = 30000;

export default class extends Controller {
  static targets = ["loading"];
  static values = {
    url: String,
    sessionUuid: String,
    variantIds: String
  };

  connect() {
    this.generation = 0;
  }

  disconnect() {
    this.generation += 1;
    clearTimeout(this.timer);
  }

  // turbo:submit-end (미리보기 폼)
  start(event) {
    if (!event.detail.success) return;

    // 지금 입력칸 값이 아니라 실제로 보낸 글자로 묻는다(잡은 그 글자로 결과를 남긴다).
    const text = event.detail.formSubmission?.fetchRequest?.body?.get?.("text");
    if (!text?.trim()) return;

    this.generation += 1;
    clearTimeout(this.timer);

    const generation = this.generation;
    const startedAt = Date.now();
    const delay = this.socketConnected() ? WAIT_FOR_SOCKET_MS : WAIT_FOR_RENDER_MS;

    this.timer = setTimeout(() => this.poll(text, generation, startedAt), delay);
  }

  async poll(text, generation, startedAt) {
    if (generation !== this.generation || !this.hasLoadingTarget) return;

    if (Date.now() - startedAt > GIVE_UP_MS) {
      this.giveUp();
      return;
    }

    try {
      const response = await fetch(this.resultUrl(text), {
        headers: { Accept: "text/vnd.turbo-stream.html" },
        credentials: "same-origin"
      });

      // 기다리는 사이 새 글자로 다시 요청했다면 이 결과는 버린다.
      if (generation !== this.generation) return;

      if (response.status === 200) {
        window.Turbo.renderStreamMessage(await response.text());
      }
    } catch (error) {
      // 네트워크 오류는 다음 차례에 다시 묻는다.
    }

    if (generation !== this.generation) return;

    // Turbo 는 받은 스트림을 다음 프레임에 반영한다. 다 받았는지는 다음 차례에 본다.
    this.timer = setTimeout(() => this.poll(text, generation, startedAt), POLL_INTERVAL_MS);
  }

  giveUp() {
    this.loadingTargets.forEach((element) => {
      element.removeAttribute("data-market--preview-fallback-target");
      element.querySelector(".animate-spin")?.remove();
      element.querySelector(".animate-pulse")?.classList.remove("animate-pulse");
      element.querySelector("[data-preview-failed-message]")?.removeAttribute("hidden");
    });
  }

  socketConnected() {
    return this.element.querySelector("turbo-cable-stream-source")?.hasAttribute("connected") ?? false;
  }

  resultUrl(text) {
    const url = new URL(this.urlValue, window.location.origin);
    url.searchParams.set("session_uuid", this.sessionUuidValue);
    url.searchParams.set("text", text);
    url.searchParams.set("variant_ids", this.variantIdsValue);
    return url;
  }
}
